/**
 * Adapter factory
 * Creates appropriate adapters based on mode (standalone vs embedded)
 */

import type { ChatAdapters, IEnvironmentConfig } from './types'
import { StandaloneAuthProvider } from './StandaloneAuthProvider'
import { StandaloneApiClient } from './StandaloneApiClient'
import { StandaloneSocketProvider } from './StandaloneSocketProvider'
import { EmbeddedAuthProvider, type EmbeddedAuthConfig } from './EmbeddedAuthProvider'
import { EmbeddedApiClient, type EmbeddedApiConfig } from './EmbeddedApiClient'
import { EmbeddedSocketProvider, type EmbeddedSocketConfig } from './EmbeddedSocketProvider'
import { getEnvironmentConfig } from './environment'

/**
 * Configuration for embedded mode
 */
export interface EmbeddedAdapterConfig {
  auth: EmbeddedAuthConfig
  api: EmbeddedApiConfig
  socket: EmbeddedSocketConfig
  env: IEnvironmentConfig
}

/**
 * Create adapters for standalone mode
 */
export const createStandaloneAdapters = (domain?: string): ChatAdapters => {
  const env = getEnvironmentConfig()
  const authProvider = new StandaloneAuthProvider()
  
  let baseURL: string | undefined;
  let socketURL: string | undefined;
  
  if (domain) {
    const cleanDomain = domain.replace(/^https?:\/\//, '').replace(/\/+$/, '');
    const isLocalhost = cleanDomain.includes('localhost') || cleanDomain.includes('127.0.0.1');
    const protocol = isLocalhost ? 'http' : 'https';
    const wsProtocol = isLocalhost ? 'ws' : 'wss';
    
    baseURL = `${protocol}://${cleanDomain}`;
    socketURL = `${wsProtocol}://${cleanDomain}/ws/chat/v2/`;
  }
  
  const apiClient = new StandaloneApiClient(() => authProvider.getToken(), baseURL)
  const socketProvider = new StandaloneSocketProvider(socketURL)

  return {
    auth: authProvider,
    api: apiClient,
    socket: socketProvider,
    env
  }
}

/**
 * Create adapters for embedded mode
 */
export const createEmbeddedAdapters = (config: EmbeddedAdapterConfig): ChatAdapters => {
  return {
    auth: new EmbeddedAuthProvider(config.auth),
    api: new EmbeddedApiClient(config.api),
    socket: new EmbeddedSocketProvider(config.socket),
    env: config.env
  }
}

/**
 * Auto-detect mode and create appropriate adapters
 */
export const createAdapters = (embeddedConfig?: EmbeddedAdapterConfig, domain?: string): ChatAdapters => {
  if (embeddedConfig) {
    return createEmbeddedAdapters(embeddedConfig)
  }

  return createStandaloneAdapters(domain)
}
